/** @prettier */

import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { loadTreatment } from '@surfline/web-common/dist/features/redux';
import { ForecastGraphs } from '@surfline/quiver-react';
import ForecastTable from '../../components/ForecastTable';
import ForecastGraphsCTA from '../../components/ForecastGraphsCTA';
import ForecastDataViewToggle from '../../components/ForecastDataViewToggle';
import ForecastTimeViewDropdown from '../../components/ForecastTimeViewDropdown';
import ForecastTimeViewToggle from '../../components/ForecastTimeViewToggle';
import ChartRangeSelector from '../../components/ChartRangeSelector';
import ChangeUnits from '../../components/ChangeUnits';
import SwellTrendSlider from '../../components/SwellTrendSlider';
import { getUserDetails, getPremiumStatus } from '../../selectors/user';
import getPathname from '../../selectors/router';
import { getSubregionOverviewData } from '../../selectors/subregion';
import { trackEvent } from '../../actions/segmentEvents';
import {
  fetchGraphConditionForecast,
  fetchGraphWaveForecast,
  fetchGraphWaveForecastMaxHeights,
  fetchGraphTideForecast,
  fetchGraphWeatherForecast,
  fetchGraphWindForecast,
} from '../../actions/graphs';
import {
  getChartSunlightTimes,
  getChartDaySummary,
  getChartWave,
  getWaveMaxHeights,
  getChartTide,
  getChartWeather,
  getChartWind,
} from '../../selectors/graphs';
import devicePropType from '../../propTypes/device';
import userDetailsPropType from '../../propTypes/userDetails';
import sunlightTimesPropType from '../../propTypes/sunlightTimes';
import summaryPropType from '../../propTypes/summary';
import waveChartPropType from '../../propTypes/waveChart';
import waveMaxHeightsPropType from '../../propTypes/waveMaxHeights';
import tideChartPropType from '../../propTypes/tideChart';
import weatherChartPropType from '../../propTypes/weatherChart';
import windChartPropType from '../../propTypes/windChart';
import overviewDataPropShape from '../../propTypes/subregionOverviewData';
import daysAndRanges from './daysAndRanges';
import config from '../../config';

const SEVENTEEN_DAY = '17day';
const HOURLY = 'hourly';
const GRAPH = 'GRAPH';
const TABLE = 'TABLE';
const REGIONAL = 'REGIONAL';
const SPOT = 'SPOT';
const OPTIMAL = 'OPTIMAL';
const SWELL_HEIGHT = 'SWELL_HEIGHT';
const ONE_DAY = '1-day';

class ForecastDataVisualizer extends PureComponent {
  static propTypes = {
    device: devicePropType.isRequired,
    type: PropTypes.oneOf([SPOT, REGIONAL]).isRequired,
    category: PropTypes.string.isRequired,
    pageName: PropTypes.string.isRequired,
    spotId: PropTypes.string.isRequired,
    spotName: PropTypes.string,
    subregionId: PropTypes.string,
    subregionData: overviewDataPropShape,
    extended: PropTypes.bool,
    utcOffset: PropTypes.number.isRequired,
    isPremium: PropTypes.bool.isRequired,
    userDetails: userDetailsPropType,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    doFetchGraphConditionForecast: PropTypes.func.isRequired,
    doFetchGraphWaveForecast: PropTypes.func.isRequired,
    doFetchGraphWaveForecastMaxHeights: PropTypes.func.isRequired,
    doFetchGraphTideForecast: PropTypes.func.isRequired,
    doFetchGraphWeatherForecast: PropTypes.func.isRequired,
    doFetchGraphWindForecast: PropTypes.func.isRequired,
    doTrackEvent: PropTypes.func.isRequired,
    doLoadTreatments: PropTypes.func.isRequired,
    sunlightTimes: sunlightTimesPropType.isRequired,
    wave: waveChartPropType.isRequired,
    waveMaxHeights: waveMaxHeightsPropType.isRequired,
    tide: tideChartPropType.isRequired,
    weather: weatherChartPropType.isRequired,
    wind: windChartPropType.isRequired,
    summary: summaryPropType.isRequired,
    canToggleDataView: PropTypes.bool,
    canToggleTimeView: PropTypes.bool,
    lastUpdate: PropTypes.string,
    spotPage: PropTypes.bool,
  };

  static defaultProps = {
    spotName: null,
    spotPage: false,
    extended: false,
    subregionId: null,
    lastUpdate: null,
    canToggleDataView: false,
    canToggleTimeView: false,
    userDetails: null,
    subregionData: null,
  };

  constructor(props) {
    super(props);

    const { isPremium, extended } = this.props;
    const tableDaysAllowed = this.daysAllowed(isPremium, extended);
    const tableDaysEnabled = this.daysEnabled(tableDaysAllowed, isPremium);

    this.state = {
      timeView: SEVENTEEN_DAY,
      dataView: GRAPH,
      graphs: {
        now: new Date() / 1000,
        selectedStartDay: 0,
        previousStartDay: 0,
        sliderRangeStart: 0,
        activeSwell: null,
        isTabbing: false,
        isSlider: false,
      },
      table: {
        days: [...Array(Math.min(tableDaysAllowed, tableDaysEnabled + 1))].map((_, day) => ({
          blurred: day >= tableDaysEnabled,
          expanded: day >= tableDaysEnabled,
        })),
        filterTop3Swells: true,
      },
    };
  }

  async componentDidMount() {
    return this.fetchAllData();
  }

  async componentDidUpdate(prevProps) {
    const {
      spotId,
      device: { width },
      canToggleTimeView,
    } = this.props;
    const { tabletLargeWidth } = config;
    const { timeView, dataView } = this.state;
    const spotChanged = prevProps.spotId !== spotId;
    const wasDesktop = prevProps.device.width > tabletLargeWidth;
    const isMobile = width < tabletLargeWidth;
    const passedBreakpoint = wasDesktop && isMobile;
    const isGraph = dataView === GRAPH;
    const isHourly = timeView === HOURLY;
    const resizeShouldTriggerToggle = passedBreakpoint && isGraph && isHourly && canToggleTimeView;
    if (spotChanged) {
      this.fetchAllData();
    }
    if (resizeShouldTriggerToggle) {
      this.toggleTimeView(SEVENTEEN_DAY, '17 Day');
    }
  }

  setActiveSwell = activeSwellIndex => {
    const { graphs } = this.state;
    this.setState({
      graphs: {
        ...graphs,
        activeSwell: activeSwellIndex,
      },
    });
  };

  getCurrentRange = () => {
    const { graphs, timeView } = this.state;
    const {
      device: { width },
      extended,
      isPremium,
      canToggleTimeView,
    } = this.props;

    const isExtended = canToggleTimeView ? timeView === SEVENTEEN_DAY : extended;
    const { days } = daysAndRanges(
      isPremium,
      isExtended,
      width,
      graphs.selectedStartDay,
      graphs.isTabbing
    );
    return { start: days[0], end: days[days.length - 1] };
  };

  /**
   * @param {boolean} timeView - optionally passed for use when toggling between hourly and 17day
   * this argument allows to override the current props and explicitly request 17 days or 24 hour
   * data
   */
  fetchAllData = timeView => {
    const {
      isPremium,
      extended,
      type,
      doFetchGraphConditionForecast,
      doFetchGraphTideForecast,
      doFetchGraphWaveForecast,
      doFetchGraphWaveForecastMaxHeights,
      doFetchGraphWeatherForecast,
      doFetchGraphWindForecast,
    } = this.props;
    const isExtended = timeView ? timeView === SEVENTEEN_DAY : extended;
    const days = this.daysAllowed(isPremium, isExtended);
    const intervalHours = isExtended ? 3 : 1;
    const waveInterval = 1;
    const desired17DayWaveInterval = 6;
    const desired17DayInterval = 3;
    const windInterval = 1;
    const weatherInterval = 1;
    if (isExtended && type === REGIONAL) doFetchGraphConditionForecast(days);
    doFetchGraphWaveForecast(days, waveInterval, desired17DayWaveInterval);
    doFetchGraphWaveForecastMaxHeights(17, desired17DayWaveInterval, true);
    doFetchGraphWindForecast(days, windInterval, desired17DayInterval);
    doFetchGraphTideForecast(days, intervalHours);
    doFetchGraphWeatherForecast(days, weatherInterval, desired17DayInterval);
  };

  daysAllowed = (isPremium, extended) => {
    const extendedDays = isPremium ? 17 : 6;
    return extended ? extendedDays : 1;
  };

  daysEnabled = (daysAllowed, isPremium, isHourly) => {
    if (isHourly) return isPremium ? 5 : 2;
    if (daysAllowed !== 6) return 17;
    if (!isPremium) return 2;
    return 3;
  };

  handleSelectRange = (selectedRange, isSlider = false, sliderRangeStart, clicked = false) => {
    let action;
    if (isSlider && clicked) {
      action = 'Clicked Day';
    } else if (isSlider) {
      action = 'Slide Range';
    } else {
      action = 'Change Range';
    }
    const { graphs } = this.state;
    this.trackSelectRange(selectedRange, action);
    this.setState({
      graphs: {
        ...graphs,
        isTabbing: false,
        selectedStartDay: selectedRange.start,
        previousStartDay: graphs.selectedStartDay,
        sliderRangeStart,
        isSlider,
      },
    });
  };

  trackSelectRange = (range, buttonAction) => {
    const { device, type, doTrackEvent } = this.props;
    const mobileView = device.width < config.tabletLargeWidth;
    const forecastPeriod = mobileView
      ? `${range.start + 1}`
      : `${range.start + 1}-${range.end + 1}`;

    const category = type === SPOT ? 'spot forecast' : 'regional forecast';

    doTrackEvent('Clicked Forecast Range', {
      forecastPeriod,
      category,
      is_mobile_view: mobileView,
      buttonAction,
    });
  };

  trackChartToggle = view => {
    const { doTrackEvent, pageName, spotId, subregionId, type } = this.props;
    doTrackEvent('Toggled Forecast View', {
      category: type === SPOT ? 'spot forecast' : 'regional forecast',
      subregionId,
      spotId,
      pageName,
      view,
    });
  };

  lolaHeaderClassName = () => {
    const { timeView } = this.state;
    return classNames({
      'sl-forecast-data-visualizer__header__lola': true,
      'sl-forecast-data-visualizer__header__lola--hourly': timeView === HOURLY,
    });
  };

  className = extended => {
    const { isPremium, spotPage } = this.props;
    return classNames({
      'sl-forecast-data-visualizer': true,
      'sl-forecast-data-visualizer--extended': !!extended,
      'sl-forecast-data-visualizer--premium': isPremium,
      'sl-forecast-data-visualizer--spot-page': spotPage,
    });
  };

  headerClassName = extended => {
    const { spotPage } = this.props;
    const { dataView } = this.state;
    return classNames({
      'sl-forecast-data-visualizer__header': true,
      'sl-forecast-data-visualizer__header--extended': !!extended,
      'sl-forecast-data-visualizer__header--spot-page': spotPage,
      'sl-forecast-data-visualizer__header--regional-page': !spotPage,
      'sl-forecast-data-visualizer__header--table-view': dataView === TABLE,
    });
  };

  toggleDataView = view => {
    this.trackChartToggle(view);
    this.setState({ dataView: view });
  };

  /**
   * @description Toggles between the time view shown on the graph page
   * and triggers relevant segment calls
   * @param {string} timeView - The desired time view to toggle to ('17day' | 'hourly')
   * @param {string} [btnLocation=false] - the button location to pass to the segment event
   * @param {number} [dayNum=false] - the day to toggle to
   * @memberof ForecastDataVisualizer
   */
  toggleTimeView = (timeView, btnLocation = false, dayNum = false) => {
    const { graphs } = this.state;

    // Have to reset to the first day when switching to hourly
    this.setState({
      timeView,
      graphs: {
        ...graphs,
        selectedStartDay: dayNum || 0,
      },
    });
    const {
      doTrackEvent,
      pageName,
      spotId,
      spotName,
      subregionId,
      subregionData,
      type,
    } = this.props;
    doTrackEvent('Toggled Spot Forecast', {
      category: type === SPOT ? 'spot forecast' : 'regional forecast',
      subregionId,
      subregionName: subregionData?.name,
      spotId,
      spotName,
      pageName,
      buttonAction: btnLocation || ONE_DAY,
      view: timeView === HOURLY ? 'Hourly Forecast' : '17-Day Spot Forecast',
    });
  };

  toggleDayExpanded = day => {
    const { table } = this.state;
    const days = [...table.days];
    days[day] = {
      ...days[day],
      expanded: !days[day].expanded,
    };
    this.setState({
      table: {
        ...table,
        days,
      },
    });
  };

  expandAllDays = expanded => {
    const { table } = this.state;
    this.setState({
      table: {
        ...table,
        days: table.days.map(day => ({
          ...day,
          expanded: day.blurred || expanded,
        })),
      },
    });
  };

  toggleFilterTop3Swells = () => {
    const { table } = this.state;
    this.setState({
      table: {
        ...table,
        filterTop3Swells: !table.filterTop3Swells,
      },
    });
  };

  tabBackwards = () => {
    const { graphs } = this.state;
    const newState = {
      graphs: {
        ...graphs,
        isTabbing: true,
        selectedStartDay:
          (graphs.sliderRangeStart ? graphs.sliderRangeStart : graphs.selectedStartDay) - 1,
        sliderRangeStart: graphs.sliderRangeStart && graphs.sliderRangeStart - 1,
      },
    };

    if (graphs.selectedStartDay > 0) {
      this.setState(newState, () => this.trackSelectRange(this.getCurrentRange(), 'Scroll Back'));
    }
  };

  tabForwards = () => {
    const { graphs } = this.state;
    const newState = {
      graphs: {
        ...graphs,
        isTabbing: true,
        selectedStartDay:
          (graphs.sliderRangeStart ? graphs.sliderRangeStart : graphs.selectedStartDay) + 1,
        sliderRangeStart: graphs.sliderRangeStart && graphs.sliderRangeStart + 1,
      },
    };

    if (graphs.selectedStartDay < 16) {
      this.setState(newState, () =>
        this.trackSelectRange(this.getCurrentRange(), 'Scroll Forward')
      );
    }
  };

  render() {
    const { dataView, graphs, table, timeView } = this.state;
    const {
      device: { mobile, width },
      device,
      type,
      category,
      pageName,
      extended,
      utcOffset,
      location: { pathname },
      isPremium,
      userDetails,
      summary,
      sunlightTimes,
      wave,
      waveMaxHeights,
      tide,
      weather,
      wind,
      spotId,
      spotName,
      subregionId,
      canToggleDataView,
      canToggleTimeView,
      spotPage,
      doTrackEvent,
      doLoadTreatments,
      subregionData,
      lastUpdate,
    } = this.props;

    const isHourly = timeView === HOURLY;
    const isExtended = canToggleTimeView ? timeView === SEVENTEEN_DAY : extended;

    const { days, ranges, daysPerRange } = daysAndRanges(
      isPremium,
      isExtended,
      width,
      graphs.selectedStartDay,
      graphs.isTabbing,
      graphs.isSlider,
      false,
      graphs.previousStartDay,
      isHourly
    );
    const changeUnitsHref = userDetails
      ? `${config.accountSettingsUrl}?redirectUrl=${pathname}`
      : `${config.createAccountUrl}?redirectUrl=${encodeURIComponent(
          `${config.surflineHost}${config.accountSettingsUrl}?redirectUrl=${config.surflineHost}${pathname}`
        )}`;

    const daysAllowed = this.daysAllowed(isPremium, isExtended);
    const daysEnabled = this.daysEnabled(daysAllowed, isPremium, isHourly);

    const isTablet = width < config.tabletLargeWidth;
    const isGraphView = dataView === GRAPH;
    const showLolaHeader =
      isTablet || isHourly || (!isGraphView && !canToggleTimeView) || !canToggleTimeView;
    const showTimeViewToggle = canToggleTimeView && isGraphView && !isTablet;
    const showTimeViewDropdown = showTimeViewToggle && width < config.rangeSelectorWidths.fourDay;
    const showChartRangeSelector = isGraphView && width && (isExtended || isHourly) && ranges;
    const showSwellTrendSlider = showChartRangeSelector && width > config.tabletLargeWidth;
    const show24HourLink = !canToggleTimeView && !isExtended;

    return (
      <div className="sl-forecast-data-visualizer-container">
        <div className={this.className(isExtended)}>
          <div className={this.headerClassName(isExtended, spotPage)}>
            {showLolaHeader && type === SPOT ? (
              <div className={this.lolaHeaderClassName()}>
                <h6>Model Forecast</h6>
                <p>Generated by LOLA</p>
              </div>
            ) : null}
            {!showTimeViewToggle &&
              canToggleDataView &&
              !(canToggleTimeView && isHourly && !mobile) && (
                <ForecastDataViewToggle dataView={dataView} onClick={this.toggleDataView} />
              )}
            {showTimeViewToggle && !showTimeViewDropdown && (
              <ForecastTimeViewToggle timeView={timeView} onClick={this.toggleTimeView} />
            )}
            {showTimeViewDropdown && (
              <ForecastTimeViewDropdown timeView={timeView} onClick={this.toggleTimeView} />
            )}
            {showChartRangeSelector && !showSwellTrendSlider ? (
              <ChartRangeSelector
                mobile={width < config.tabletLargeWidth}
                ranges={ranges}
                waveDays={wave.days}
                waveMaxHeightsDays={waveMaxHeights.days}
                summaryDays={summary.days}
                showConditions={type === REGIONAL}
                units={wave.units}
                utcOffset={utcOffset}
                selectedStartDay={graphs.selectedStartDay}
                daysEnabled={daysEnabled}
                onSelectRange={this.handleSelectRange}
                isPremium={isPremium}
                type={type}
                spotId={spotId}
                isTabbing={graphs.isTabbing}
                isHourly={isHourly}
              />
            ) : null}
            {showSwellTrendSlider && (
              <SwellTrendSlider
                daysPerRange={daysPerRange}
                days={days}
                deviceWidth={width}
                ranges={ranges}
                waveMaxHeightsDays={waveMaxHeights.days}
                utcOffset={utcOffset}
                selectedStartDay={graphs.selectedStartDay}
                daysEnabled={daysEnabled}
                onSelectRange={this.handleSelectRange}
                isPremium={isPremium}
                type={type}
                spotId={spotId}
                isTabbing={graphs.isTabbing}
                isSlider={graphs.isSlider}
                daysAndRanges={daysAndRanges}
                isHourly={isHourly}
              />
            )}
            {showTimeViewToggle &&
              canToggleDataView &&
              !(canToggleTimeView && isHourly && !mobile) && (
                <ForecastDataViewToggle dataView={dataView} onClick={this.toggleDataView} />
              )}
            {show24HourLink && (
              <div className="sl-forecast-data-visualizer__24-hour-header">
                24 Hour Forecast
                <Link
                  className="sl-forecast-data-visualizer__17-day-link"
                  to={location => `${location.pathname}/forecast`}
                  onClick={() =>
                    doTrackEvent('Clicked Link', {
                      linkUrl: `${pathname}/forecast`,
                      linkName: '17-day forcast',
                      linkLocation: '24-hour forecast',
                      category: 'cams & reports',
                    })
                  }
                >
                  View 17-day forecast
                </Link>
              </div>
            )}
          </div>
          {isGraphView ? (
            <ForecastGraphs
              isHourly={isHourly}
              extended={isExtended}
              days={days}
              daysEnabled={daysEnabled}
              type={type}
              lastUpdate={lastUpdate}
              doTrackEvent={doTrackEvent}
              device={device}
              mobile={mobile}
              category={category}
              pageName={pageName}
              spotName={spotName}
              spotId={spotId}
              subregionId={subregionId}
              subregionName={subregionData?.name}
              utcOffset={utcOffset}
              summary={summary}
              sunlightTimes={sunlightTimes}
              wave={wave}
              tide={tide}
              weather={weather}
              wind={wind}
              now={graphs.now}
              showCTA={isExtended && !isPremium}
              activeSwell={graphs.activeSwell}
              setActiveSwell={this.setActiveSwell}
              tabBackwards={this.tabBackwards}
              tabForwards={this.tabForwards}
              isPremium={isPremium}
              spotPage={spotPage}
              doLoadTreatments={doLoadTreatments}
              changeToHourlyView={(btnLocation, dayNum) =>
                this.toggleTimeView(HOURLY, btnLocation, dayNum)
              }
              CTAComponent={() => (
                <ForecastGraphsCTA
                  category={category}
                  pageName={pageName}
                  spotId={spotId}
                  subregionId={subregionId}
                />
              )}
            />
          ) : (
            <ForecastTable
              days={table.days}
              wave={wave}
              wind={wind}
              utcOffset={utcOffset}
              category={category}
              pageName={pageName}
              spotId={spotId}
              subregionId={subregionId}
              showCTA={isExtended && !isPremium}
              toggleDayExpanded={this.toggleDayExpanded}
              expandAllDays={this.expandAllDays}
              hideSurfAndWind={type === REGIONAL}
              filterTop3Swells={table.filterTop3Swells}
              toggleFilterTop3Swells={this.toggleFilterTop3Swells}
              highlightType={type === SPOT ? OPTIMAL : SWELL_HEIGHT}
              deviceWidth={width}
            />
          )}
          <ChangeUnits href={changeUnitsHref} />
        </div>
      </div>
    );
  }
}

export default withRouter(
  connect(
    (state, props) => ({
      pathname: getPathname(state),
      userDetails: getUserDetails(state),
      isPremium: getPremiumStatus(state),
      sunlightTimes: getChartSunlightTimes(state),
      summary: getChartDaySummary(state),
      wave: getChartWave(state, props),
      waveMaxHeights: getWaveMaxHeights(state, props),
      tide: getChartTide(state, props),
      weather: getChartWeather(state, props),
      wind: getChartWind(state, props),
      subregionData: getSubregionOverviewData(state),
    }),
    (dispatch, { type, spotId, subregionId }) => ({
      doFetchGraphConditionForecast: days =>
        dispatch(fetchGraphConditionForecast(subregionId, days)),
      doFetchGraphWaveForecast: (days, intervalHours, desired17DayWaveInterval) =>
        dispatch(
          fetchGraphWaveForecast(type)(
            type === REGIONAL ? subregionId : spotId,
            days,
            intervalHours,
            false,
            desired17DayWaveInterval,
            true
          )
        ),
      doFetchGraphWaveForecastMaxHeights: (
        days,
        intervalHours,
        maxHeights,
        desired17DayWaveInterval
      ) =>
        dispatch(
          fetchGraphWaveForecastMaxHeights(type)(
            type === REGIONAL ? subregionId : spotId,
            days,
            intervalHours,
            maxHeights,
            desired17DayWaveInterval
          )
        ),
      doFetchGraphTideForecast: (days, intervalHours) =>
        dispatch(fetchGraphTideForecast(spotId, days, intervalHours)),
      doFetchGraphWeatherForecast: (days, intervalHours, desired17DayInterval) =>
        dispatch(
          fetchGraphWeatherForecast(spotId, days, intervalHours, false, desired17DayInterval)
        ),
      doFetchGraphWindForecast: (days, intervalHours, desired17DayWindInterval) =>
        dispatch(
          fetchGraphWindForecast(spotId, days, intervalHours, false, desired17DayWindInterval)
        ),
      doTrackEvent: (event, properties) => dispatch(trackEvent(event, properties)),
      doLoadTreatments: treatment => dispatch(loadTreatment(treatment)),
    })
  )(ForecastDataVisualizer)
);
